import { faCalculator, faChevronDown, faChevronUp, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Alert, Badge, Button, Card, Col, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";

import { EXPLAIN_SCORES, SCORES_FILE_TYPE } from "../../../../amplify/appsync/write";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { isSectionUnread } from "../utils";

export const SectionScores = ({
    isLoading,
    data,
    patientVisit,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    latestFileVersions = undefined,
    handleShowScanMultiModal = undefined,
    unreadFileVersionMap = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [sortConfig, setSortConfig] = useState<{ column: "med" | "start" | "ome"; direction: "asc" | "desc" }>({
        column: "med",
        direction: "asc",
    });

    if (!patientVisit) {
        return null;
    }

    const omeScore = data?.scores?.find((score: any) => score.score_slug === "ome_total_daily");
    const omeData = data?.ome_data;
    const components = omeData?.components ?? omeScore?.components ?? [];
    const warnings = omeScore?.warnings ?? omeData?.warnings ?? [];
    const totalRangeText = omeScore?.range_text ?? omeData?.range_text ?? "0.0 mg/day";
    const totalDaily = omeScore?.value?.max_mg_per_day ?? omeData?.value?.max_mg_per_day ?? 0;
    const homeMedsStructured = data?.home_meds_structured ?? [];
    const isFullMedList = homeMedsStructured.length > 0;
    const displayRows = isFullMedList ? homeMedsStructured : components;

    const opioidComponentMap = new Map<string, any>();
    components.forEach((component: any) => {
        const payload = component.raw_payload || {};
        const potentialKeys = [
            payload.fhir_resource_id,
            payload.patient_med_id,
            payload.order_id,
            payload.med_name,
            component.source_id,
        ].filter(Boolean);
        potentialKeys.forEach((key: string) => {
            if (!opioidComponentMap.has(key)) {
                opioidComponentMap.set(key, component);
            }
        });
    });

    const findComponentForMed = (med: any) => {
        if (!med) {
            return null;
        }
        const potentialKeys = [
            med.fhir_resource_id,
            med.patient_med_id,
            med.order_id,
            med.med_name,
        ].filter(Boolean);
        for (const key of potentialKeys) {
            if (opioidComponentMap.has(key)) {
                return opioidComponentMap.get(key);
            }
        }
        return null;
    };

    const getOmeRiskFlag = (value: number) => {
        if (value >= 200) return "Extremely high risk";
        if (value >= 90) return "Very high risk";
        if (value >= 50) return "High risk";
        return null;
    };
    const omeRiskFlag = getOmeRiskFlag(totalDaily);
    const keyPhysiology = data?.key_physiology ?? [];

    const isAbnormal = (entry: any) => {
        if (!entry?.component_abnormal) return false;
        return entry.component_abnormal.toUpperCase() !== "NORMAL";
    };

    const formatResultTime = (raw?: string) => {
        if (!raw) return "—";
        const trimmed = raw.trim();
        const needsT = trimmed.includes(" ");
        let normalized = needsT ? trimmed.replace(" ", "T") : trimmed;
        const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(normalized);
        if (!hasTimezone) {
            normalized = `${normalized}Z`;
        }
        const parsed = Date.parse(normalized);
        if (Number.isNaN(parsed)) {
            return raw;
        }
        return new Date(parsed).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleSort = (column: "med" | "start" | "ome") => {
        setSortConfig((prev) => {
            if (prev.column === column) {
                return { column, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { column, direction: "asc" };
        });
    };

    const renderSortIndicator = (column: "med" | "start" | "ome") => {
        if (sortConfig.column !== column) return null;
        return sortConfig.direction === "asc" ? <span className="ms-1">▲</span> : <span className="ms-1">▼</span>;
    };

    const parseTimestamp = (raw?: string) => {
        if (!raw) return null;
        const trimmed = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
        const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(trimmed);
        const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
        const parsed = Date.parse(normalized);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const buildRowEntries = () => {
        return displayRows.map((row: any, idx: number) => {
            const component = isFullMedList ? findComponentForMed(row) : row;
            const payload = isFullMedList ? row : (component?.raw_payload || {});
            return {
                original: row,
                payload,
                component,
                key: payload.fhir_resource_id || payload.patient_med_id || component?.source_id || payload.med_name || idx,
            };
        });
    };

    const compareEntries = (a: any, b: any, column: "med" | "start" | "ome") => {
        const getString = (entry: any) => (entry.payload.med_name || entry.payload.med_subclass || entry.component?.med_display || "").toLowerCase();
        const getStart = (entry: any) => parseTimestamp(entry.payload.start_date || entry.payload.order_date);
        const getOme = (entry: any) => {
            if (entry.component) {
                if (typeof entry.component.ome_daily_max === "number") return entry.component.ome_daily_max;
                if (typeof entry.component.ome_daily_min === "number") return entry.component.ome_daily_min;
            }
            return null;
        };

        const compareValues = (valA: any, valB: any, isString = false) => {
            if (valA === valB) return 0;
            if (valA === null || typeof valA === "undefined") return 1;
            if (valB === null || typeof valB === "undefined") return -1;
            if (isString) {
                return valA.localeCompare(valB);
            }
            return valA < valB ? -1 : 1;
        };

        if (column === "med") {
            return compareValues(getString(a), getString(b), true);
        }
        if (column === "start") {
            return compareValues(getStart(a), getStart(b));
        }
        return compareValues(getOme(a), getOme(b));
    };

    const sortedEntries = buildRowEntries().sort((a: any, b: any) => {
        const result = compareEntries(a, b, sortConfig.column);
        return sortConfig.direction === "asc" ? result : -result;
    });

    const renderValueCell = (entry: any) => {
        if (!entry?.component_value) {
            return <span className="text-muted">Not available</span>;
        }
        return (
            <span className="fw-semibold text-nowrap">
                {entry.component_value}
            </span>
        );
    };

    return (
        <Card className="mt-3">
            <FakeLoadingBar isActive={isLoading} maxDuration={12000} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className="dashboard-actions-circle-small dashboard-actions-circle-blue me-2">
                                <FontAwesomeIcon icon={faCalculator} fixedWidth />
                            </span>
                            <strong>Scores</strong>
                            {isSectionUnread(SCORES_FILE_TYPE, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        <LLMDashboardUpdated
                            patient={patientVisit}
                            statuses={statuses}
                            handleShowScanMultiModal={handleShowScanMultiModal}
                            latestFileVersions={latestFileVersions}
                            explainKey={EXPLAIN_SCORES}
                            sectionToSetter={sectionToSetter}
                            handleFileVersionChange={handleFileVersionChange}
                        />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip>Refresh Section</Tooltip>}
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => { e.stopPropagation(); setRegenerateTarget(EXPLAIN_SCORES); setShowContextMenu(true); }}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>

                {!isLoading && !data && (
                    <Row className="mt-3">
                        <Col>
                            <Alert variant="secondary" className="mb-0">
                                Scores have not been generated for this visit yet.
                            </Alert>
                        </Col>
                    </Row>
                )}

                {isExpanded && data && (
                    <>
                        <div className="mt-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex flex-column flex-md-row align-items-md-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted text-uppercase small">Total OME</div>
                                            <div className="fs-5 fw-semibold">
                                                {totalRangeText}
                                                {omeRiskFlag && <span className="badge bg-danger ms-2">{omeRiskFlag}</span>}
                                            </div>
                                        </div>
                                        <div className="text-md-end text-muted small">
                                            Version {omeScore?.version ?? omeData?.calculator_version ?? "2025-02-20"}
                                        </div>
                                    </div>
                                    {warnings && warnings.length > 0 && (
                                        <div className="mt-2">
                                            {warnings.map((warning: any) => (
                                                <Badge bg="warning" text="dark" className="me-2" key={warning.code || warning}>
                                                    {warning.code || warning}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-3">
                                        {displayRows.length === 0 ? (
                                            <Alert variant="light" className="mb-0">
                                                {isFullMedList ? "No structured home medications available." : "No opioids detected in structured home medications."}
                                            </Alert>
                                        ) : (
                                            <Table bordered responsive size="sm" className="mb-0 small">
                                                <thead>
                                                    <tr>
                                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("med")}>
                                                            Medication {renderSortIndicator("med")}
                                                        </th>
                                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("start")}>
                                                            Start {renderSortIndicator("start")}
                                                        </th>
                                                        <th className="text-end" style={{ cursor: "pointer" }} onClick={() => handleSort("ome")}>
                                                            OME range (mg/day) {renderSortIndicator("ome")}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedEntries.map((entry: any, idx: number) => {
                                                        const { payload, component } = entry;
                                                        const medName = payload?.med_name || payload?.med_subclass || component?.med_display || "Medication";
                                                        const sigText = payload?.patient_instruction || payload?.sig || "";
                                                        const startDate = payload?.start_date || payload?.order_date;
                                                        const formattedStart = startDate ? formatResultTime(startDate) : "";
                                                        const isOpioid = Boolean(component);
                                                        const range = component
                                                            ? (component.ome_daily_min === component.ome_daily_max
                                                                ? `${component.ome_daily_min.toFixed(1)}`
                                                                : `${component.ome_daily_min.toFixed(1)}–${component.ome_daily_max.toFixed(1)}`)
                                                            : "—";
                                                        const isPrn = Boolean(
                                                            (payload?.as_needed && String(payload.as_needed).trim()) ||
                                                            component?.prn ||
                                                            component?.raw_payload?.as_needed
                                                        );
                                                        const isNewThisVisit = Boolean(
                                                            payload?.first_ordered_this_visit ||
                                                            component?.raw_payload?.first_ordered_this_visit
                                                        );
                                                        return (
                                                            <tr key={`${entry.key || idx}`}>
                                                                <td>
                                                                    <div className={isOpioid ? "text-danger fw-semibold" : ""}>
                                                                        {medName}
                                                                        {isPrn && <Badge bg="secondary" className="ms-2">PRN</Badge>}
                                                                        {isNewThisVisit && <Badge bg="warning" text="dark" className="ms-2">NEW THIS VISIT</Badge>}
                                                                    </div>
                                                                    {sigText && <div className="text-muted fst-italic small mt-1">{sigText}</div>}
                                                                </td>
                                                                <td>{formattedStart}</td>
                                                                <td className={`text-end ${isOpioid ? "text-danger fw-semibold" : ""}`}>{range}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex flex-column flex-md-row align-items-md-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted text-uppercase small">Key physiology</div>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        {keyPhysiology.length === 0 ? (
                                            <Alert variant="light" className="mb-0">
                                                Key physiology labs are unavailable for this visit.
                                            </Alert>
                                        ) : (
                                            <div className="small">
                                                {keyPhysiology.map((entry: any, idx: number) => (
                                                    <div key={entry.slug} className={`py-2 ${idx !== keyPhysiology.length - 1 ? "border-bottom" : ""}`}>
                                                        <strong>{entry.label}:</strong>{" "}
                                                        <span className={`${isAbnormal(entry) ? "text-danger fw-semibold" : "text-muted"}`}>
                                                            {renderValueCell(entry)}
                                                            {entry.component_units && <span className="ms-1">{entry.component_units}</span>}
                                                            {entry.component_abnormal && isAbnormal(entry) && <span className="ms-1">({entry.component_abnormal})</span>}
                                                        </span>
                                                        {entry.result_time && (
                                                            <span className="text-muted ms-2">· {formatResultTime(entry.result_time)}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
